import { createContext, useRef, useEffect, useCallback, useState, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setUser, logout } from "../slice/authSlice";
import authConfig from "../auth/auth";
import { getToken, setToken, clearToken } from "../auth/tokenStore";
import useRefreshToken from "../hooks/useRefreshToken";
import { initializeInterceptors } from "../api/axios";
import { getIsRefreshing, setIsRefreshing } from "../utils/refreshState";

const AuthContext = createContext({
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  deviceId: null,
  errorMessage: "",
  setErrorMessage: () => {},
});

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const refresh = useRefreshToken();

  const [isLoading, setIsLoading] = useState(true);
  const [deviceId, setDeviceId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const initializeRef = useRef(false);

  useEffect(() => {
    initializeInterceptors(refresh);
  }, [refresh]);

  // 앱 초기화 (Device ID 설정)
  useEffect(() => {
    const deviceIdKey = "device_id";
    let id = localStorage.getItem(deviceIdKey);

    if (!id) {
      id = uuidv4();
      localStorage.setItem(deviceIdKey, id);
      console.log("새로운 Device ID 생성:", id);
    } else {
      console.log("기존 Device ID 사용:", id);
    }

    setDeviceId(id);
  }, []);

  // ✅ deviceId가 설정된 후에만 initializeAuth 실행
  useEffect(() => {
    if (!deviceId || initializeRef.current) return;
    initializeRef.current = true;

    const initializeAuth = async () => {
      const currentToken = getToken();
      if (!currentToken && !getIsRefreshing()) {
        try {
          setIsRefreshing(true)
          await refresh(deviceId); // deviceId 전달
        } catch (error) {
          clearToken();
          dispatch(logout());
        } finally {
          setIsRefreshing(false);
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [deviceId, refresh, dispatch]);

  useEffect(() => {
    console.log("유저확인:", user);
  }, [user]);

  const handleLogin = useCallback(
    async (params) => {
      try {
        const response = await axios.post(authConfig.loginEndpoint, params, {
          withCredentials: true,
        });
        setToken(response.data.accessToken);
        dispatch(setUser(response.data.user));
        setErrorMessage("");
        const currentGroupCode = response.data.user?.group_code || "";
        const groupLoginPagePath = currentGroupCode ? `/login/${currentGroupCode}` : "/";

        // 로그인 페이지 경로를 쿠키 저장
        document.cookie = `groupLoginPagePath=${groupLoginPagePath}; path=/; max-age=31536000`;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          setErrorMessage("아이디 또는 비밀번호를 확인해주세요.");
        } else {
          setErrorMessage("로그인 중 문제가 발생했습니다.");
        }
      }
    },
    [dispatch]
  );

  const handleLogout = useCallback(async () => {
    try {
      // 로그아웃 시 리다이렉트 저장 방지 플래그 설정
      sessionStorage.setItem("preventRedirect", "true");
      
      await axios.post(authConfig.logoutEndpoint, {}, { withCredentials: true });

      clearToken();
      dispatch(logout());
      
      // 세션 스토리지 정리
      sessionStorage.removeItem("redirectPath");
      
      // 쿠키에서 그룹 로그인 페이지 확인
      const cookies = document.cookie.split("; ").reduce((acc, cookie) => {
        const [key, value] = cookie.split("=");
        acc[key] = value;
        return acc;
      }, {});
      const groupLoginPagePath = cookies.groupLoginPagePath || "/";

      navigate(groupLoginPagePath);

    } catch (error) {
      console.error("로그아웃 중 오류:", error);
      setErrorMessage("로그아웃에 문제가 발생했습니다.");
    }
  }, [navigate, dispatch]);

  const values = useMemo(
    () => ({
      login: handleLogin,
      logout: handleLogout,
      deviceId,
      errorMessage,
      setErrorMessage,
    }),
    [handleLogin, handleLogout, deviceId, errorMessage]
  );

  return (
    <AuthContext.Provider value={values}>
      {isLoading ? <p>Loading...</p> : children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };